/**
 * Single source of truth for analytics consent.
 * Reads from the same `cookie-consent` localStorage key used by CookieConsent.tsx,
 * exposes a tiny pub/sub so loaders can react to grant/revoke.
 */

const STORAGE_KEY = 'cookie-consent';

export interface ConsentState {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

export const defaultConsent: ConsentState = {
  essential: true,
  analytics: false,
  marketing: false,
  functional: false,
};

export const readConsent = (): ConsentState => {
  if (typeof window === 'undefined') return defaultConsent;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultConsent;
    const parsed = JSON.parse(raw) as Partial<ConsentState>;
    return { ...defaultConsent, ...parsed, essential: true };
  } catch {
    return defaultConsent;
  }
};

export const hasAnalyticsConsent = (): boolean => readConsent().analytics === true;

type Listener = (consent: ConsentState) => void;
const listeners = new Set<Listener>();

export const subscribeConsent = (fn: Listener): (() => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/** Call this whenever consent is updated by the banner. */
export const notifyConsentChanged = (next?: ConsentState) => {
  const state = next ?? readConsent();
  listeners.forEach((l) => {
    try {
      l(state);
    } catch (e) {
      console.warn('[analytics] consent listener error', e);
    }
  });
};

// React to changes from other tabs.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) notifyConsentChanged();
  });
}
